import gql from 'graphql-tag';
import { omit } from 'lodash';

import apiv2Client from '@/apiv2Client';

import * as types from './mutation-types';
import { ungroupMediaQuestions } from './helpers';

// eslint-disable-next-line
const whitelistedFields = (whitelist, obj) =>
  Object.keys(obj).reduce((acc, key) => {
    if (whitelist.includes(key)) {
      return { ...acc, [key]: obj[key] };
    }
    return acc;
  }, {});

const sortByFavorite = surveyData =>
  surveyData.sort((a, b) => {
    if (a.favorite === b.favorite) {
      return 0;
    } else if (a.favorite) {
      return -1;
    }
    return 1;
  });

const surveyWhitelist = [
  // id, shouldn't be here it's set as a separate parameter on survey update
  'title',
  'description',
  'startDate',
  'endDate',
  'successText',
  'successRedirect',
  'successAutoRedirect',
  'privacyPolicy',
  'collectNonVoluntaryIdentifiers',
  'logoUrl',
  'logoImage',
  'surveyType',
  'isPreview',
  'isActive',
  'accentColor',
  'config',
  'favorite',
  'deleted',
  'pageSize',
  'createdById',
];

const questionWhitelist = [
  'id',
  'categoryId',
  'chart',
  'choices',
  'matrixChoices',
  'insight',
  'isChoiceOrderReversed',
  'isGlobal',
  'isProfile',
  'isRequired',
  'profileField',
  'questionType',
  'title',
  'visibilityCondition',
  'mediaClipId',
];

const choiceTemplateWhitelist = [
  'id',
  'title',
  'description',
  'categoryId',
  'choices',
  'isGlobal',
  'keywords',
];

const queryChoices = async (
  filters = {},
  orderedByChoiceTemplate = null,
  page = 1,
  pageSize = 25,
) =>
  apiv2Client.query({
    query: gql`
      query(
        $filter: String
        $categoryId: Int
        $orderedByChoiceTemplate: Int
        $pagination: PaginationInput
      ) {
        surveysListChoice(
          filter: $filter
          categoryId: $categoryId
          orderedByChoiceTemplate: $orderedByChoiceTemplate
          pagination: $pagination
        ) {
          entries {
            id
            title
            polarity
          }
          pageNumber
          totalPages
        }
      }
    `,
    variables: {
      ...filters,
      orderedByChoiceTemplate,
      pagination: {
        page_size: pageSize,
        page,
      },
    },
  });

const queryChoiceTemplates = async (
  filters = {},
  orderedByMatchedKeywords = '',
  page = 1,
  pageSize = 25,
) =>
  apiv2Client.query({
    query: gql`
      query(
        $filter: String
        $categoryId: Int
        $orderedByMatchedKeywords: String
        $pagination: PaginationInput
      ) {
        surveysListChoiceTemplate(
          filter: $filter
          categoryId: $categoryId
          orderedByMatchedKeywords: $orderedByMatchedKeywords
          pagination: $pagination
        ) {
          entries {
            id
            title
            description
            keywords
            categoryId
            isGlobal
            choices {
              id
              title
              polarity
            }
          }
          pageNumber
          totalPages
        }
      }
    `,
    variables: {
      ...filters,
      orderedByMatchedKeywords,
      pagination: {
        page_size: pageSize,
        page,
      },
    },
  });

const queryQuestions = async (filters = {}, page = 1, pageSize = 25) =>
  apiv2Client.query({
    query: gql`
      query(
        $filter: String
        $categoryId: Int
        $excludeQuestionTypes: [String]
        $pagination: PaginationInput
      ) {
        surveysListQuestion(
          filter: $filter
          categoryId: $categoryId
          excludeQuestionTypes: $excludeQuestionTypes
          pagination: $pagination
        ) {
          entries {
            id
            title
            questionType
            chart
            categoryId
            isGlobal
          }
          pageNumber
          totalPages
        }
      }
    `,
    variables: {
      ...filters,
      excludeQuestionTypes: ['MEDIA'],
      pagination: {
        page_size: pageSize,
        page,
      },
    },
    fetchPolicy: 'network-only',
  });

const queryHooks = async (filters = {}, page = 1, pageSize = 25) =>
  apiv2Client.query({
    query: gql`
      query($filter: String, $pagination: PaginationInput) {
        surveysListMediaClip(filter: $filter, pagination: $pagination) {
          entries {
            id
            artist
            title
            description
            fileUrl
          }
          pageNumber
          totalPages
        }
      }
    `,
    variables: {
      ...filters,
      pagination: {
        page_size: pageSize,
        page,
      },
    },
  });

const queryMatrixChoices = async (filters = {}, page = 1, pageSize = 25) =>
  apiv2Client.query({
    query: gql`
      query($filter: String, $pagination: PaginationInput) {
        surveysListMatrixChoice(filter: $filter, pagination: $pagination) {
          entries {
            id
            title
            visibleIf
            isRequired
            choices {
              id
              title
            }
          }
          pageNumber
          totalPages
        }
      }
    `,
    variables: {
      ...filters,
      pagination: {
        page_size: pageSize,
        page,
      },
    },
  });

export default {
  // Survey
  async listSurveys({ commit }, payload = {}) {
    const filters = payload.filters || {};
    const page = payload.page || 1;
    const pageSize = payload.pageSize || 25;
    commit(types.LIST_SURVEYS_REQUEST);

    try {
      const res = await apiv2Client.query({
        query: gql`
          query($state: SurveyState, $title: String, $pagination: PaginationInput) {
            surveysListSurvey(state: $state, title: $title, pagination: $pagination) {
              entries {
                id
                title
                description
                startDate
                endDate
                createdBy {
                  firstName
                  lastName
                }
                url
                successText
                successRedirect
                successAutoRedirect
                privacyPolicy
                collectNonVoluntaryIdentifiers
                accentColor
                logoUrl
                logoImage
                isActive
                isPreview
                surveyType
                favorite
                totalEntries
              }
              pageNumber
              totalPages
            }
          }
        `,
        variables: {
          ...filters,
          pagination: {
            page,
            page_size: pageSize,
          },
        },
      });
      commit(types.LIST_SURVEYS_SUCCESS, {
        surveys: sortByFavorite(res.data.surveysListSurvey.entries),
        pageNumber: res.data.surveysListSurvey.pageNumber,
        totalPages: res.data.surveysListSurvey.totalPages,
      });
    } catch (error) {
      commit(types.LIST_SURVEYS_FAILURE, error);
    }
  },

  async listSurveysGoToPage({ dispatch, state }, page) {
    dispatch('listSurveys', { page, filter: state.surveyListFilter });
  },

  async listSurveysNextPage({ dispatch, state }) {
    dispatch('listSurveysGoToPage', state.surveyListPage + 1);
  },

  async listSurveysPrevPage({ dispatch, state }) {
    dispatch('listSurveysGoToPage', state.surveyListPage - 1);
  },

  async getSurvey({ commit }, surveyId) {
    commit(types.GET_SURVEY_REQUEST);

    try {
      const res = await apiv2Client.query({
        query: gql`
          query($surveyId: Int!) {
            surveysGetSurvey(surveyId: $surveyId) {
              id
              title
              description
              startDate
              endDate
              url
              successText
              successRedirect
              successAutoRedirect
              privacyPolicy
              collectNonVoluntaryIdentifiers
              accentColor
              logoUrl
              logoImage
              isActive
              isPreview
              surveyType
              config
              pageSize
              questions {
                id
                title
                questionType
                chart
                categoryId
                insight
                isRequired
                isGlobal
                isProfile
                profileField
                isChoiceOrderReversed
                visibilityCondition
                mediaClip {
                  id
                  title
                  artist
                  type
                  fileUrl
                }
                choices {
                  id
                  title
                }
                choiceLinks {
                  choiceId
                  order
                  polarity
                }
                matrixChoices {
                  id
                  name
                  title
                  visibleIf
                  isRequired
                  choices {
                    id
                    title
                  }
                }
                matrixChoiceLinks  {
                  id
                  order
                  matrixChoiceId
                }
              }
            }
          }
        `,
        variables: {
          surveyId: parseInt(surveyId, 10),
        },
      });
      const survey = res.data.surveysGetSurvey;
      commit(types.GET_SURVEY_SUCCESS, survey);

      const questions = survey.questions || [{ id: null }];
      commit(types.SELECT_CANVAS_QUESTION_ID, questions[0].id);
    } catch (error) {
      commit(types.GET_SURVEY_FAILURE, error);
    }
  },

  // Questions
  async getQuestions({ commit, getters, state }, page = 1) {
    const filters = getters.selectorQuestionFilters;
    commit(types.GET_QUESTIONS_REQUEST);
    try {
      const res = await queryQuestions(filters, page, state.selectorPageSize);
      commit(types.GET_QUESTIONS_SUCCESS, res.data.surveysListQuestion);
    } catch (error) {
      commit(types.GET_QUESTIONS_FAILURE, error);
    }
  },

  async getMoreQuestions({ commit, getters, state }) {
    const filters = getters.selectorQuestionFilters;
    const nextPage = state.questionsPage + 1;
    if (nextPage > state.questionsTotalPages) {
      return;
    }

    commit(types.APPEND_QUESTIONS_REQUEST);
    try {
      const res = await queryQuestions(filters, nextPage, state.selectorPageSize);
      commit(types.APPEND_QUESTIONS_SUCCESS, res.data.surveysListQuestion);
    } catch (error) {
      commit(types.APPEND_QUESTIONS_FAILURE, error);
    }
  },

  async createQuestion({ commit, dispatch }, question) {
    commit(types.CREATE_QUESTION_REQUEST);
    try {
      const res = await apiv2Client.mutate({
        mutation: gql`
          mutation($question: SurveysQuestionInput!) {
            surveysCreateQuestion(question: $question) {
              id
              title
              questionType
              chart
              categoryId
              insight
              isRequired
              isGlobal
              isProfile
              profileField
              visibilityCondition
            }
          }
        `,
        variables: {
          question: whitelistedFields(questionWhitelist, question),
        },
      });
      commit(types.CREATE_QUESTION_SUCCESS, res.data.surveysCreateQuestion);
    } catch (error) {
      commit(types.CREATE_QUESTION_FAILURE, error);
    }
  },

  async updateQuestion({ commit, dispatch }, question) {
    commit(types.UPDATE_QUESTION_REQUEST);
    try {
      const res = await apiv2Client.mutate({
        mutation: gql`
          mutation($questionId: Int!, $question: SurveysQuestionInput!) {
            surveysUpdateQuestion(questionId: $questionId, question: $question) {
              id
              title
              questionType
              chart
              categoryId
              insight
              isRequired
              isGlobal
              isProfile
              profileField
              isChoiceOrderReversed
              visibilityCondition
              choices {
                id
                title
              }
              choiceLinks {
                choiceId
                polarity
                order
              }
            }
          }
        `,
        variables: {
          question: whitelistedFields(questionWhitelist, question),
          questionId: question.id,
        },
      });
      commit(types.UPDATE_QUESTION_SUCCESS, res.data.surveysUpdateQuestion);
    } catch (error) {
      commit(types.UPDATE_QUESTION_FAILURE, error);
    }
  },

  async getChoices({ commit, getters, state }, page = 1) {
    const filters = getters.selectorChoiceFilters;
    commit(types.GET_CHOICES_REQUEST);
    try {
      const templateId = state.selectorSelectedTemplateId || null;
      const res = await queryChoices(filters, templateId, page, state.selectorPageSize);
      commit(types.GET_CHOICES_SUCCESS, res.data.surveysListChoice);
    } catch (error) {
      commit(types.GET_CHOICES_FAILURE, error);
    }
  },

  async getMoreChoices({ commit, getters, state }) {
    const filters = getters.selectorChoiceFilters;
    const nextPage = state.choicesPage + 1;
    if (nextPage > state.choicesTotalPages) {
      return;
    }

    commit(types.APPEND_CHOICES_REQUEST);
    try {
      const res = await queryChoices(filters, nextPage, state.selectorPageSize);
      commit(types.APPEND_CHOICES_SUCCESS, res.data.surveysListChoice);
    } catch (error) {
      commit(types.APPEND_CHOICES_FAILURE, error);
    }
  },

  async getChoiceTemplates({ commit, getters, state }, page = 1) {
    const filters = getters.selectorChoiceTemplateFilters;
    commit(types.GET_CHOICE_TEMPLATES_REQUEST);
    try {
      const selectedQuestion = getters.canvasSelectedQuestion;
      const keywordText = selectedQuestion ? selectedQuestion.title : '';
      const res = await queryChoiceTemplates(filters, keywordText, page, state.selectorPageSize);
      commit(types.GET_CHOICE_TEMPLATES_SUCCESS, res.data.surveysListChoiceTemplate);
    } catch (error) {
      commit(types.GET_CHOICE_TEMPLATES_FAILURE, error);
    }
  },

  async getMoreChoiceTemplates({ commit, getters, state }) {
    const filters = getters.selectorChoiceTemplateFilters;
    commit(types.APPEND_CHOICE_TEMPLATES_REQUEST);
    const nextPage = state.choiceTemplatesPage + 1;
    if (nextPage > state.choiceTemplatesTotalPages) {
      return;
    }

    try {
      const keywordText = getters.canvasSelectedQuestion.title;
      const res = await queryChoiceTemplates(
        filters,
        keywordText,
        nextPage,
        state.selectorPageSize,
      );
      commit(types.APPEND_CHOICE_TEMPLATES_SUCCESS, res.data.surveysListChoiceTemplate);
    } catch (error) {
      commit(types.APPEND_CHOICE_TEMPLATES_FAILURE, error);
    }
  },

  async createChoiceTemplate({ commit }, choiceTemplate) {
    commit(types.CREATE_CHOICE_TEMPLATE_REQUEST);
    try {
      const res = await apiv2Client.mutate({
        mutation: gql`
          mutation($choiceTemplate: SurveysChoiceTemplateInput!) {
            surveysCreateChoiceTemplate(choiceTemplate: $choiceTemplate) {
              id
              title
              description
              keywords
              categoryId
              isGlobal
              choices {
                id
                title
                polarity
              }
            }
          }
        `,
        variables: {
          choiceTemplate: whitelistedFields(choiceTemplateWhitelist, choiceTemplate),
        },
      });
      commit(types.CREATE_CHOICE_TEMPLATE_SUCCESS, res.data.surveysCreateChoiceTemplate);
    } catch (error) {
      commit(types.CREATE_CHOICE_TEMPLATE_FAILURE, error);
    }
  },

  async updateChoiceTemplate({ commit }, choiceTemplate) {
    commit(types.UPDATE_CHOICE_TEMPLATE_REQUEST);
    try {
      const choiceTemplateWithoutChoices = omit(choiceTemplate, ['choices']);
      const res = await apiv2Client.mutate({
        mutation: gql`
          mutation($choiceTemplateId: Int!, $choiceTemplate: SurveysChoiceTemplateInput!) {
            surveysUpdateChoiceTemplate(
              choiceTemplateId: $choiceTemplateId
              choiceTemplate: $choiceTemplate
            ) {
              id
              title
              description
              keywords
              categoryId
              isGlobal
              choices {
                id
                title
                polarity
              }
            }
          }
        `,
        variables: {
          choiceTemplate: whitelistedFields(choiceTemplateWhitelist, choiceTemplateWithoutChoices),
          choiceTemplateId: choiceTemplate.id,
        },
      });
      commit(types.UPDATE_CHOICE_TEMPLATE_SUCCESS, res.data.surveysUpdateChoiceTemplate);
    } catch (error) {
      commit(types.UPDATE_CHOICE_TEMPLATE_FAILURE, error);
    }
  },

  // Hooks
  async getHooks({ commit, getters, state }, page = 1) {
    const filters = getters.selectorHookFilters;
    commit(types.GET_HOOKS_REQUEST);
    try {
      const res = await queryHooks(filters, page, state.selectorPageSize);
      commit(types.GET_HOOKS_SUCCESS, res.data.surveysListMediaClip);
    } catch (error) {
      commit(types.GET_HOOKS_FAILURE, error);
    }
  },

  async getMoreHooks({ commit, getters, state }) {
    const filters = getters.selectorHookFilters;
    const nextPage = state.hooksPage + 1;
    if (nextPage > state.hooksTotalPages) {
      return;
    }

    commit(types.APPEND_HOOKS_REQUEST);
    try {
      const res = await queryHooks(filters, nextPage, state.selectorPageSize);
      commit(types.APPEND_HOOKS_SUCCESS, res.data.surveysListHook);
    } catch (error) {
      commit(types.APPEND_HOOKS_FAILURE, error);
    }
  },

  // Matrix choices (Music questions)
  async getMatrixChoices({ commit, getters, state }, page = 1) {
    const filters = getters.selectorMatrixChoiceFilters;
    commit(types.GET_MATRIX_CHOICES_REQUEST);
    try {
      const res = await queryMatrixChoices(filters, page, state.selectorPageSize);
      commit(types.GET_MATRIX_CHOICES_SUCCESS, res.data.surveysListMatrixChoice);
    } catch (error) {
      commit(types.GET_MATRIX_CHOICES_FAILURE, error);
    }
  },

  async getMoreMatrixChoices({ commit, getters, state }) {
    const filters = getters.selectorMatrixChoiceFilters;
    const nextPage = state.hooksPage + 1;
    if (nextPage > state.hooksTotalPages) {
      return;
    }

    commit(types.APPEND_MATRIX_CHOICES_REQUEST);
    try {
      const res = await queryMatrixChoices(filters, nextPage, state.selectorPageSize);
      commit(types.APPEND_MATRIX_CHOICES_SUCCESS, res.data.surveysListMatrixChoice);
    } catch (error) {
      commit(types.APPEND_MATRIX_CHOICES_FAILURE, error);
    }
  },

  async getQuestionCategories({ commit }) {
    commit(types.GET_QUESTION_CATEGORIES_REQUEST);
    try {
      const res = await apiv2Client.query({
        query: gql`
          {
            surveysListQuestionCategory {
              id
              title
            }
          }
        `,
      });
      const categories = res.data.surveysListQuestionCategory;
      commit(types.GET_QUESTION_CATEGORIES_SUCCESS, categories);
    } catch (error) {
      commit(types.GET_QUESTION_CATEGORIES_FAILURE, error);
    }
  },

  async getQuestionCategoriesWithQuestions({ commit }) {
    commit(types.GET_QUESTION_CATEGORIES_REQUEST);
    try {
      const res = await apiv2Client.query({
        query: gql`
          {
            surveysListQuestionCategory(onlyWithQuestions: true) {
              id
              title
            }
          }
        `,
      });
      const categories = res.data.surveysListQuestionCategory;
      commit(types.GET_QUESTION_CATEGORIES_WITH_QUESTIONS_SUCCESS, categories);
    } catch (error) {
      commit(types.GET_QUESTION_CATEGORIES_WITH_QUESTIONS_FAILURE, error);
    }
  },

  async createQuestionCategory({ commit, dispatch }, questionCategory) {
    commit(types.CREATE_QUESTION_CATEGORY_REQUEST);
    try {
      const res = await apiv2Client.mutate({
        mutation: gql`
          mutation($questionCategory: SurveysQuestionCategoryInput!) {
            surveysCreateQuestionCategory(questionCategory: $questionCategory) {
              id
              title
              description
            }
          }
        `,
        variables: {
          questionCategory,
        },
      });
      commit(types.CREATE_QUESTION_CATEGORY_SUCCESS, res.data.surveysCreateQuestionCategory);
    } catch (error) {
      commit(types.CREATE_QUESTION_CATEGORY_FAILURE, error);
    }
  },

  async createChoiceCategory({ commit, dispatch }, choiceCategory) {
    commit(types.CREATE_CHOICE_CATEGORY_REQUEST);
    try {
      const res = await apiv2Client.mutate({
        mutation: gql`
          mutation($choiceCategory: SurveysChoiceCategoryInput!) {
            surveysCreateChoiceCategory(choiceCategory: $choiceCategory) {
              id
              title
              description
            }
          }
        `,
        variables: {
          choiceCategory,
        },
      });
      commit(types.CREATE_CHOICE_CATEGORY_SUCCESS, res.data.surveysCreateChoiceCategory);
    } catch (error) {
      commit(types.CREATE_CHOICE_CATEGORY_FAILURE, error);
    }
  },

  async getChoiceCategories({ commit }) {
    commit(types.GET_CHOICE_CATEGORIES_REQUEST);
    try {
      const res = await apiv2Client.query({
        query: gql`
          {
            surveysListChoiceCategory {
              id
              title
            }
          }
        `,
      });
      const categories = res.data.surveysListChoiceCategory;
      commit(types.GET_CHOICE_CATEGORIES_SUCCESS, categories);
    } catch (error) {
      commit(types.GET_CHOICE_CATEGORIES_FAILURE, error);
    }
  },

  async surveyCreate({ commit }, survey) {
    commit(types.CREATE_SURVEY_REQUEST);
    try {
      const response = await apiv2Client.mutate({
        mutation: gql`
          mutation($surveyInput: SurveysSurveyInput) {
            surveysCreate(survey: $surveyInput) {
              id
              title
              description
              startDate
              endDate
              url
              successText
              successRedirect
              successAutoRedirect
              privacyPolicy
              collectNonVoluntaryIdentifiers
              accentColor
              logoUrl
              logoImage
              isActive
              isPreview
              surveyType
              createdById
            }
          }
        `,
        variables: { surveyInput: survey },
      });
      commit(types.CREATE_SURVEY_SUCCESS, response);
    } catch (error) {
      commit(types.CREATE_SURVEY_FAILURE, error);
    }
  },

  async surveyUpdate({ commit }, survey) {
    commit(types.UPDATE_SURVEY_REQUEST);

    try {
      const response = await apiv2Client.mutate({
        mutation: gql`
          mutation($surveyId: Int, $surveyInput: SurveysSurveyInput) {
            surveysUpdate(surveyId: $surveyId, survey: $surveyInput) {
              id
              title
              description
              startDate
              endDate
              url
              successText
              successRedirect
              successAutoRedirect
              privacyPolicy
              collectNonVoluntaryIdentifiers
              accentColor
              logoUrl
              logoImage
              isActive
              isPreview
              surveyType
              createdById
              config
              questions {
                id
                title
                questionType
                chart
                categoryId
                insight
                isRequired
                isGlobal
                isProfile
                profileField
                isChoiceOrderReversed
                visibilityCondition
                mediaClip {
                  id
                  title
                  artist
                  type
                  fileUrl
                }
                choices {
                  id
                  title
                }
                choiceLinks {
                  choiceId
                  polarity
                  order
                }
                matrixChoices {
                  id
                  name
                  title
                  visibleIf
                  isRequired
                  choices {
                    id
                    title
                  }
                }
              }
            }
          }
        `,
        variables: { surveyId: survey.id, surveyInput: whitelistedFields(surveyWhitelist, survey) },
      });
      commit(types.UPDATE_SURVEY_SUCCESS, response);
    } catch (error) {
      commit(types.UPDATE_SURVEY_FAILURE, error);
    }
  },

  async surveyUpdateConfig({ dispatch, state }, config) {
    dispatch('surveyUpdate', { ...state.survey, config: JSON.stringify(config) });
  },

  async updateCanvasQuestions({ dispatch, state }) {
    dispatch('updateQuestions', { questions: state.canvasQuestions, surveyId: state.survey.id });
  },

  async updateQuestions({ commit, state }, { questions, surveyId }) {
    if (!state.survey.isPreview) {
      return;
    }

    commit(types.UPDATE_CANVAS_QUESTIONS_REQUEST);
    try {
      let processedQuestions = questions;

      processedQuestions = ungroupMediaQuestions(processedQuestions);

      // Set default chart type if no chart type set
      processedQuestions = processedQuestions.map(question => {
        const chart = question.chart === '' ? 'VERTICAL_BAR' : question.chart;
        return { ...question, chart };
      });

      // Remove fields we don't need
      processedQuestions = processedQuestions.map(question => {
        const choices = question.choices || [];
        const updatedChoices = choices.map(choice => {
          const omitFields = ['templateId', 'conditionalLink'];
          if (isNaN(choice.id)) {
            omitFields.push('id');
          }
          return omit(choice, omitFields);
        });

        return {
          ...whitelistedFields(questionWhitelist, question),
          choices: updatedChoices,
        };
      });

      const res = await apiv2Client.mutate({
        mutation: gql`
          mutation($surveyId: Int, $questions: SurveysQuestionInput) {
            surveysUpdateQuestions(surveyId: $surveyId, questions: $questions) {
              id
              title
              questions {
                id
                title
                questionType
                chart
                categoryId
                insight
                isRequired
                isGlobal
                isProfile
                profileField
                isChoiceOrderReversed
                visibilityCondition
                mediaClip {
                  id
                  title
                  artist
                  type
                  fileUrl
                }
                choices {
                  id
                  title
                }
                choiceLinks {
                  choiceId
                  polarity
                  order
                }
                matrixChoices {
                  id
                  name
                  title
                  visibleIf
                  isRequired
                  choices {
                    id
                    title
                  }
                }
                matrixChoiceLinks  {
                  id
                  order
                  matrixChoiceId
                }
              }
            }
          }
        `,
        variables: {
          surveyId,
          questions: processedQuestions,
        },
      });
      commit(types.UPDATE_CANVAS_QUESTIONS_SUCCESS, res.data.surveysUpdateQuestions);
    } catch (error) {
      commit(types.UPDATE_CANVAS_QUESTIONS_FAILURE, error);
    }
  },

  async createMatrixChoice({ commit, state }, { matrixChoice }) {
    commit(types.CREATE_MATRIX_CHOICE_REQUEST);
    try {
      const res = await apiv2Client.mutate({
        mutation: gql`
          mutation($matrixChoice: SurveysMatrixChoiceInput!) {
            surveysCreateMatrixChoice(matrixChoice: $matrixChoice) {
              id
              title
              name
              visibleIf
              isRequired
              choices {
                id
                title
                polarity
              }
            }
          }
        `,
        variables: {
          matrixChoice: {
            title: matrixChoice.title,
            choices: matrixChoice.choices,
          },
        },
      });
      commit(types.CREATE_MATRIX_CHOICE_SUCCESS, res.data.surveysCreateMatrixChoice);
    } catch (error) {
      commit(types.CREATE_MATRIX_CHOICE_FAILURE, error);
    }
  },

  async createHook({ commit, state }, { hook }) {
    commit(types.CREATE_HOOK_REQUEST);
    try {
      const res = await apiv2Client.mutate({
        mutation: gql`
          mutation($mediaClip: SurveysMediaClipInput!) {
            surveysCreateMediaClip(mediaClip: $mediaClip) {
              id
              artist
              title
              genre
              description
              type
              fileUrl
            }
          }
        `,
        variables: {
          mediaClip: {
            title: hook.title,
            artist: hook.artist,
            description: hook.description,
            type: 'audio',
            fileData: hook.fileData,
          },
        },
      });
      commit(types.CREATE_HOOK_SUCCESS, res.data.surveysCreateMediaClip);
    } catch (error) {
      commit(types.CREATE_HOOK_FAILURE, error);
    }
  },

  async updateHook({ commit, state }, { hook }) {
    commit(types.UPDATE_HOOK_REQUEST);
    try {
      const res = await apiv2Client.mutate({
        mutation: gql`
          mutation($mediaClipId: Int!, $mediaClip: SurveysMediaClipInput!) {
            surveysUpdateMediaClip(
              mediaClipId: $mediaClipId,
              mediaClip: $mediaClip
              ) {
              id
              artist
              title
              genre
              description
              type
              fileUrl
            }
          }
        `,
        variables: {
          mediaClipId: hook.id,
          mediaClip: {
            title: hook.title,
            artist: hook.artist,
            description: hook.description,
            type: 'audio',
            fileData: hook.fileData,
          },
        },
      });
      commit(types.UPDATE_HOOK_SUCCESS, res.data.surveysUpdateMediaClip);
    } catch (error) {
      commit(types.UPDATE_HOOK_FAILURE, error);
    }
  },

  async updateMatrixChoice({ commit, state }, matrixChoice) {
    commit(types.UPDATE_MATRIX_CHOICE_REQUEST);
    try {
      const res = await apiv2Client.mutate({
        mutation: gql`
          mutation($matrixChoiceId: Int!, $matrixChoice: SurveysMatrixChoiceInput!) {
            surveysUpdateMatrixChoice(
              matrixChoiceId: $matrixChoiceId
              matrixChoice: $matrixChoice
            ) {
              id
              name
              title
              visibleIf
              isRequired
              choices {
                id
                title
                polarity
              }
            }
          }
        `,
        variables: {
          matrixChoiceId: matrixChoice.id,
          matrixChoice: {
            name: matrixChoice.name,
            title: matrixChoice.title,
            visibleIf: matrixChoice.visibleIf,
            isRequired: matrixChoice.isRequired,
            choices: matrixChoice.choices,
          },
        },
      });
      commit(types.UPDATE_MATRIX_CHOICE_SUCCESS, res.data.surveysUpdateMatrixChoice);
    } catch (error) {
      commit(types.UPDATE_MATRIX_CHOICE_FAILURE, error);
    }
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/surveybuilder/api-actions.js